// Die Orte der Fundstellen — deterministisch, aus einer **eigenen** Folge.
//
// **Wortgleiche Entsprechung von `backend/farmining/world/caches.py`** — bis auf
// den Hinweistext, und der steht mit Absicht nur hier (siehe unten).
//
// Eine Fundstelle ist eine Kiste, die im tiefen Raum treibt. Sie ist der
// Gegenentwurf zum Nebenauftrag: dort zeigt die Karte den Punkt, hier nennt ein
// Hinweis nur Richtung und Entfernung, und das Suchen ist der Inhalt.
//
// Die Bezugsgroesse ist die **Tabellenposition**, nicht eine Kachel — die Folge
// beruehrt damit keine Erzziehung (Gates FG-20, FG-30). Einen eigenen
// Golden-Block hat sie trotzdem: beide Seiten muessen denselben Punkt treffen,
// sonst sucht der Spieler dort, wo der Server nichts bucht.
//
// Diese Datei importiert **kein Phaser** (Gate FG-18).

import { balancing, cacheIndexOf, zonesFor } from '../data/balancing';
import { gearAt, gearFor, hasGear, type GearSave } from './gear';
import type { Cache } from '../data/types';
import { TAU, clamp, jsRound } from '../core/math';
import { hintOf } from '../core/format';
import { hash3, rngFrom } from '../core/rng';
import { eraSeed } from './field';
import type { Point } from './sidequests';

/**
 * Der Ring, in dem die Fundstelle mit dieser Position liegt.
 *
 * **Die Kette führt nach außen.** Jede weitere Fundstelle liegt einen festen
 * Schritt weiter draußen; gestreut wird nur innerhalb ihres Rings. Aus einer
 * festen Schrittweite und nicht aus „Tiefe geteilt durch Anzahl": sonst
 * verschöbe eine neue Kiste **alle** bestehenden.
 */
export function cacheBand(index: number, era: number): [number, number] {
  const zones = zonesFor(era);
  // Der tiefe Raum **ist** die äußerste Zone — dieselbe Ableitung wie
  // `deep_zone_index` im Backend, und keine getippte Zahl: in Akt II wäre sie
  // schon eine andere.
  const inner = zones[zones.length - 1].from + Math.max(0, index) * balancing().cache.step;
  return [inner, inner + balancing().cache.step];
}

/**
 * Der Ort einer Fundstelle. **Die Reihenfolge der Ziehungen ist Vertrag.**
 *
 * Schreibt in einen übergebenen Puffer (Gate FG-14).
 */
export function cachePoint(index: number, seed: number, era: number, out: Point): Point {
  const welt = eraSeed(seed, era);
  const rng = rngFrom(hash3(hash3(index, 0, welt), balancing().cache.salt, welt));

  const angle = rng() * TAU;                                            // 1
  const span = rng();                                                   // 2

  const [inner, outer] = cacheBand(index, era);
  const spread = balancing().cache.spread;
  const mitte = (1 - spread) * 0.5;
  const radius = inner + (outer - inner) * (mitte + spread * span);
  out.x = jsRound(Math.cos(angle) * radius);
  out.y = jsRound(Math.sin(angle) * radius);
  return out;
}

/**
 * Die Tabellenposition einer Fundstelle — sie geht in die Saat ein.
 *
 * Über eine Nachschlagekarte, nicht über eine lineare Suche: `cacheAt` läuft je
 * Bild für jede sichtbare Kiste (`balancing.md`, „Lesezugriff läuft **nie** über
 * eine lineare Suche").
 */
export const cacheIndex = (id: string): number => cacheIndexOf(id);

/** Der Ort einer konkreten Fundstelle. */
export function cacheAt(cache: Cache, seed: number, era: number, out: Point): Point {
  return cachePoint(cacheIndex(cache.id), seed, era, out);
}

const puffer: Point = { x: 0, y: 0 };

/** Steht das Schiff nah genug an dieser Fundstelle? */
export function atCache(x: number, y: number, cache: Cache, seed: number, era: number): boolean {
  cacheAt(cache, seed, era, puffer);
  return Math.hypot(puffer.x - x, puffer.y - y) <= balancing().cache.reach;
}

// ── Der Hinweis ──────────────────────────────────────────────────────────────

/**
 * Der Hinweis auf eine Fundstelle — **aus ihrem Ort gerechnet, nie umgekehrt.**
 *
 * Das ist die tragende Entscheidung der Fundstellen. Stünde der Text in der
 * Tabelle und der Ort würde daraus gezogen, wären beide zwei Wahrheiten, und der
 * Text wäre die, die still lügt: „35 000 nach Norden", und da ist nichts. So
 * herum kann der Hinweis nicht falsch sein — er ist eine Aussage **über** die
 * Zahl, die auch die Kiste hinstellt.
 *
 * Deshalb nennt er gerundete Richtung und Entfernung und nicht die Koordinate.
 * Die Koordinate wäre kein Hinweis, sondern ein Wegpunkt, und das Suchen ist der
 * Inhalt (auf der Karte steht bewusst nichts).
 *
 * Er lebt nur im Client: der Server braucht ihn nicht, und deutscher Anzeigetext
 * zweimal wäre zweimal zu pflegen.
 */
export function cacheHint(cache: Cache, seed: number, era: number): string {
  cacheAt(cache, seed, era, puffer);
  // Die Rundung steht **einmal** in `core/format.ts`: sie stand hier und in
  // `gear.ts` Zeile für Zeile gleich, und mit dem Wächter wäre sie dreimal da.
  return hintOf(puffer.x, puffer.y);
}

// ── Der Kettenstand ──────────────────────────────────────────────────────────

/** Der Spielstand, soweit eine Fundstelle ihn liest. */
export interface CacheSave extends GearSave {
  caches: Record<string, boolean>;
  /** Ab wann eine geborgene Kiste wieder etwas hergibt — je ID ein Zeitpunkt. */
  cacheReady: Record<string, number>;
}

export const cacheFound = (save: CacheSave, cache: Cache): boolean =>
  Boolean(save.caches[cache.id]);

/**
 * Die Fundstellen, die in dieser Ära überhaupt liegen.
 *
 * Der Riegel wirkt **nur nach vorn**, wie bei den Aufträgen: eine Fundstelle aus
 * einer früheren Ära bleibt findbar. Eine, die verschwände, weil jemand
 * heimgeflogen ist, wäre ein Hinweis auf eine Kiste, die es nicht mehr gibt.
 */
export const cachesFor = (save: CacheSave): Cache[] =>
  balancing().caches.filter((cache) => cache.minEra <= save.era);

/**
 * Die nächste ungeborgene Fundstelle — **der offene Hinweis**.
 *
 * Abgeleitet und nicht als eigenes Feld im Spielstand: zwei Quellen für denselben
 * Kettenstand können auseinanderlaufen, und dann zeigt der Hinweis auf eine
 * Kiste, die schon geborgen ist. **Wortgleich zu `rules.open_cache`.**
 */
export function openCache(save: CacheSave): Cache | null {
  for (const cache of cachesFor(save)) if (!cacheFound(save, cache)) return cache;
  return null;
}

/** Ein geborgener Fund, wie ihn beide Fassungen melden. */
export interface CacheFind {
  id: string;
  first: boolean;
  res: Record<string, number>;
}

/**
 * Fundstellen am Ort `(x, y)` bergen.
 *
 * **Nur die offene und die schon geborgenen.** Die Kette ist eine Reihenfolge:
 * ohne den Hinweis der vorigen Kiste weiß niemand, wo die nächste liegt, und wer
 * zufällig darüber fliegt, soll sie nicht überspringen.
 *
 * Sie **nimmt nie etwas** (FG-29) und läuft nie ab. Die Solo-Fassung von
 * `rules.find_caches`; dass es zwei gibt, ist der Preis dafür, dass das Spiel
 * ohne Backend spielbar bleibt, und dass beide dasselbe tun, sichern dieselben
 * Testfälle auf beiden Seiten.
 *
 * Sie **bucht nicht** — sie setzt nur die Marke und sagt, was fällig ist. Der
 * Aufrufer legt die Ressourcen auf, weil das online der Server tut.
 */
export function findCaches(
  save: CacheSave, x: number, y: number, seed: number, out: CacheFind[],
  now: number = Date.now() / 1000,
): CacheFind[] {
  out.length = 0;
  const offen = openCache(save);
  const share = balancing().cache.repeatShare;
  const frist = balancing().cache.cooldown;
  for (const cache of cachesFor(save)) {
    const first = !cacheFound(save, cache);
    if (first && cache !== offen) continue;
    // Ein Wiederfund ist ein **Ereignis**, kein Zustand: diese Funktion läuft je
    // Bild, und ohne die Frist zahlte eine geborgene Kiste im Sekundentakt.
    if (!first && now < (save.cacheReady[cache.id] ?? 0)) continue;
    // **Ein Wandschrank passt in keine Luke.** Wortgleich zu `rules.find_caches`:
    // ohne das verlangte Fundstück bleibt die Kiste liegen und nimmt nichts
    // (FG-29). Warum, sagt `blockedCacheAt` — ohne Nachricht, aus denselben
    // Daten.
    if (!hasGear(save, cache.needsGear)) continue;
    if (!atCache(x, y, cache, seed, save.era)) continue;
    save.cacheReady[cache.id] = now + frist;
    const anteil = first ? 1 : share;
    const res: Record<string, number> = {};
    for (const [id, amount] of Object.entries(cache.reward)) res[id] = amount * anteil;
    if (first) save.caches[cache.id] = true;
    out.push({ id: cache.id, first, res });
  }
  return out;
}

/**
 * Die Fundstelle, vor der der Spieler gerade steht und die er **nicht** heben
 * kann — oder `null`.
 *
 * Sie beantwortet die Frage, die der Server gar nicht beantworten muss: er lässt
 * den Fund einfach aus. Ohne diese Rechnung stünde der Spieler vor einem
 * blinkenden Werkzeugschrank, bei dem nichts passiert, und suchte den Fehler bei
 * sich. Beide Seiten rechnen dieselbe Antwort aus denselben Daten — es braucht
 * **keine Nachricht** dafür, dieselbe Bauform wie der Gefechtschip
 * (`simulation.md`).
 *
 * Sie liegt hier und nicht in `sim/gear.ts`, weil sie eine Frage über **Kisten**
 * ist: dort müsste sie `atCache` importieren, während `findCaches` hier den
 * Geräteriegel braucht — ein Zyklus (Gate FG-05).
 */
export function blockedCacheAt(
  save: CacheSave, x: number, y: number, seed: number,
): { cache: Cache; gearId: string } | null {
  for (const cache of cachesFor(save)) {
    if (!cache.needsGear || hasGear(save, cache.needsGear)) continue;
    if (!atCache(x, y, cache, seed, save.era)) continue;
    return { cache, gearId: cache.needsGear };
  }
  return null;
}

// ── Das Suchsignal ───────────────────────────────────────────────────────────

/** Wie nah der nächste auffindbare Fund ist — und wie schnell es deshalb pingt. */
export interface CacheSignal {
  /** Abstand in Welteinheiten. */
  dist: number;
  /** Sekunden bis zum nächsten Ton. Kurz heißt nah. */
  interval: number;
  /** 0 am Rand der Hörweite, 1 am Fundradius — für die Stärke des Rings. */
  strength: number;
}

/**
 * Der nächste Fund in Hörweite — **Nähe, nie Richtung**.
 *
 * Das ist die Zeile, an der die ganze Sucherei hängt. Der Hinweis nennt die
 * Richtung auf ein Achtel genau und die Entfernung auf 500 m; bei 20 900 m
 * Abstand bleibt ein Bogen von rund 16 000 m zu suchen, und die Kiste selbst ist
 * erst auf ein paar hundert Meter zu sehen. Ohne Signal fliegt man den Bogen ab,
 * sieht nichts und war zweimal fast darüber.
 *
 * Es trägt bewusst **keine Richtung**. Ein Pfeil wäre ein Zielgerät und nähme
 * genau das weg, wofür die Funde nicht auf der Karte stehen — wer es hört, weiß
 * „hier irgendwo", nicht „dort".
 *
 * Gemeldet wird nur, was sich **jetzt** aufnehmen ließe: der offene Hinweis und
 * die noch nicht geholten Fundstücke. Eine geborgene Kiste schweigt — sie gibt
 * zwar noch einen Rest her, aber das Signal ist ein Suchwerkzeug und kein
 * Wegweiser zurück. Und eine Kiste weiter hinten in der Kette schweigt auch:
 * sie ließe sich gar nicht öffnen, und ein Ton, dem nichts folgt, ist ein
 * Fehler, der nach einem Fehler aussieht.
 *
 * Ein gesperrter schwerer Fund pingt dagegen sehr wohl — man soll ihn finden
 * und dann erfahren, dass der Verladearm fehlt (`blockedCacheAt`).
 *
 * Schreibt in einen übergebenen Puffer und allokiert nichts: sie läuft je Bild
 * (Gate FG-14).
 */
export function cacheSignal(
  save: CacheSave, x: number, y: number, seed: number, out: CacheSignal,
): boolean {
  const { reach, signalRange, signalSlow, signalFast } = balancing().cache;
  let naechster = Infinity;

  const offen = openCache(save);
  if (offen) {
    cacheAt(offen, seed, save.era, puffer);
    naechster = Math.hypot(puffer.x - x, puffer.y - y);
  }
  for (const gear of gearFor(save)) {
    if (save.gear[gear.id]) continue;
    gearAt(gear, seed, save.era, puffer);
    naechster = Math.min(naechster, Math.hypot(puffer.x - x, puffer.y - y));
  }

  if (naechster > signalRange) return false;

  // Innerhalb des Fundradius ist der Takt am schnellsten und wird nicht mehr
  // kuerzer: naeher als „gefunden" geht es nicht.
  const spanne = Math.max(1, signalRange - reach);
  const anteil = clamp((naechster - reach) / spanne, 0, 1);
  out.dist = naechster;
  out.strength = 1 - anteil;
  out.interval = signalFast + (signalSlow - signalFast) * anteil;
  return true;
}
